import React, { useState, useEffect, useRef } from 'react';
import api from '../api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { FileText, Upload, Plus, Sparkles, Loader2, Trash2, Zap, Shield, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

const SCENARIO_BADGE_CONFIG = {
  positive: { label: 'Positive', className: 'bg-emerald-100 text-emerald-700' },
  negative: { label: 'Negative', className: 'bg-red-100 text-red-700' },
  edge: { label: 'Edge', className: 'bg-amber-100 text-amber-700' },
  security: { label: 'Security', className: 'bg-violet-100 text-violet-700' },
  validation: { label: 'Validation', className: 'bg-blue-100 text-blue-700' },
};

const ScenarioTypeBadge = ({ type }) => {
  const config = SCENARIO_BADGE_CONFIG[type] || SCENARIO_BADGE_CONFIG.positive;
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium whitespace-nowrap ${config.className}`} data-testid={`scenario-badge-${type}`}>
      {config.label}
    </span>
  );
};

const AddRequirementDialog = ({ projectId, project, onSuccess }) => {
  const moduleOptions = project.modules?.length > 0 ? project.modules : ['General'];
  const [formData, setFormData] = useState({ module: moduleOptions[0], title: '', description: '', acceptance_criteria: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const criteriaArray = formData.acceptance_criteria
        .split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0);
      
      const payload = {
        module: formData.module || 'General',
        title: formData.title,
        description: formData.description,
        acceptance_criteria: criteriaArray
      };
      
      await api.post(`/projects/${projectId}/requirements`, payload);
      toast.success('Requirement added successfully');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add requirement');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <DialogHeader><DialogTitle>Add Requirement</DialogTitle></DialogHeader>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Label htmlFor="module">Module</Label>
          <Select value={formData.module} onValueChange={(value) => setFormData({ ...formData, module: value })}>
            <SelectTrigger data-testid="req-module-select"><SelectValue placeholder="Select module" /></SelectTrigger>
            <SelectContent>{moduleOptions.map((mod) => (<SelectItem key={mod} value={mod}>{mod}</SelectItem>))}</SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="title">Title</Label>
          <Input id="title" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} placeholder="User login functionality" required data-testid="req-title-input" />
        </div>
        <div>
          <Label htmlFor="description">Description</Label>
          <Textarea id="description" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Detailed description..." required data-testid="req-description-input" />
        </div>
        <div>
          <Label htmlFor="acceptance_criteria">Acceptance Criteria (one per line)</Label>
          <Textarea id="acceptance_criteria" value={formData.acceptance_criteria} onChange={(e) => setFormData({ ...formData, acceptance_criteria: e.target.value })} placeholder="User can login with email&#10;System validates credentials&#10;User is redirected to dashboard" required data-testid="req-criteria-input" rows={5} />
        </div>
        <Button type="submit" disabled={loading} className="w-full" data-testid="submit-requirement-button">{loading ? 'Adding...' : 'Add Requirement'}</Button>
      </form>
    </>
  );
};

const UploadRequirementDialog = ({ projectId, project, onSuccess, usage }) => {
  const [file, setFile] = useState(null);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [extractedReqs, setExtractedReqs] = useState(null);
  const [extractionError, setExtractionError] = useState(null);
  const [jobId, setJobId] = useState(null);
  const [pollingStatus, setPollingStatus] = useState(null);
  const [extractionProgress, setExtractionProgress] = useState(null);
  
  // Refs to prevent duplicate toasts and ensure stable polling
  const pollIntervalRef = useRef(null);
  const jobCompletedRef = useRef(false);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, []);

  // Polling effect for async extraction
  useEffect(() => {
    let pollCount = 0;
    const maxPollCount = 200; // Max 10 minutes (200 * 3 seconds)
    
    // Phase-based messages for extraction
    const getPhaseMessage = (count) => {
      if (count <= 2) return 'Reading document...';
      if (count <= 8) return 'Extracting structured requirements...';
      if (count <= 20) return 'AI is analyzing content...';
      if (count <= 40) return 'Processing large document — this may take a few minutes...';
      if (count <= 80) return 'Still working — AI is extracting requirements...';
      if (count <= 150) return 'Almost there — finalizing extraction...';
      return 'Wrapping up...';
    };

    // Clear any existing interval
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }

    if (jobId && loading) {
      // Reset completed flag for new job
      jobCompletedRef.current = false;
      
      pollIntervalRef.current = setInterval(async () => {
        // Guard: Skip if already completed
        if (jobCompletedRef.current) {
          return;
        }
        
        try {
          pollCount++;
          const response = await api.get(`/extraction-status/${jobId}`);
          const data = response.data;
          
          // Double-check guard after async call
          if (jobCompletedRef.current) {
            return;
          }
          
          // Update status with real backend progress
          if (data.progress) {
            setExtractionProgress({ progress: data.progress, total: data.total_chunks, done: data.chunks_done });
          }
          if (data.phase) {
            setPollingStatus(data.phase);
          } else {
            setPollingStatus(getPhaseMessage(pollCount));
          }
          
          if (data.status === 'completed') {
            // Mark as completed FIRST to prevent duplicate processing
            if (jobCompletedRef.current) {
              return;
            }
            jobCompletedRef.current = true;
            
            // Clear interval immediately
            if (pollIntervalRef.current) {
              clearInterval(pollIntervalRef.current);
              pollIntervalRef.current = null;
            }
            
            setLoading(false);
            setJobId(null);
            setPollingStatus(null);
            setExtractionProgress(null);
            
            if (data.requirements && data.requirements.length > 0) {
              setExtractedReqs(data.requirements);
              toast.success(`Extracted ${data.requirements.length} requirement${data.requirements.length !== 1 ? 's' : ''}`);
            } else {
              toast.warning('No requirements found in the document.');
              setExtractionError({
                code: 'NO_REQUIREMENTS',
                message: 'No requirements could be identified in the document.'
              });
            }
          } else if (data.status === 'failed') {
            jobCompletedRef.current = true;
            
            if (pollIntervalRef.current) {
              clearInterval(pollIntervalRef.current);
              pollIntervalRef.current = null;
            }
            
            setLoading(false);
            setJobId(null);
            setPollingStatus(null);
            setExtractionProgress(null);
            
            setExtractionError({
              code: data.error_code,
              message: data.error_message
            });
            if (data.error_code === 'BUDGET_EXCEEDED') {
              toast.error('AI budget exceeded. Go to Profile → Universal Key → Add Balance to continue.');
            } else {
              toast.error(data.error_message || 'Extraction failed');
            }
          } else if (pollCount >= maxPollCount) {
            jobCompletedRef.current = true;
            
            if (pollIntervalRef.current) {
              clearInterval(pollIntervalRef.current);
              pollIntervalRef.current = null;
            }
            
            setLoading(false);
            setJobId(null);
            setPollingStatus(null);
            
            toast.error('Extraction timed out. Please try a smaller document.');
            setExtractionError({
              code: 'TIMEOUT',
              message: 'Extraction took too long. Please try with a smaller document.'
            });
          }
        } catch (error) {
          console.error('Polling error:', error);
          // Don't stop polling on network errors, just log
        }
      }, 3000); // Poll every 3 seconds
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [jobId, loading]);

  const handleExtract = async () => {
    if (!file && !text) {
      toast.error('Please upload a file or enter text');
      return;
    }
    
    setLoading(true);
    setExtractionError(null);
    setPollingStatus('Starting extraction...');
    
    try {
      const formData = new FormData();
      if (file) {
        formData.append('file', file);
      } else {
        formData.append('text', text);
      }
      
      const response = await api.post(`/projects/${projectId}/requirements/extract`, formData, { 
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 30000 // 30 second timeout for initial request (just creates job)
      });
      
      const data = response.data;
      
      if (data.success === false) {
        // Immediate failure (validation errors)
        setLoading(false);
        setPollingStatus(null);
        setExtractionError({
          code: data.error_code,
          message: data.message
        });
        
        switch (data.error_code) {
          case 'TEXT_TOO_LONG':
            toast.error(`Document too large (${data.text_length?.toLocaleString()} chars). Max: ${data.max_allowed?.toLocaleString()} chars.`);
            break;
          case 'EMPTY_EXTRACTION':
            toast.error('Could not extract text. The file may be scanned or empty.');
            break;
          default:
            toast.error(data.message || 'Failed to extract requirements');
        }
        return;
      }
      
      // Check if async mode
      if (data.async && data.job_id) {
        // Start polling
        setJobId(data.job_id);
        setPollingStatus('AI is extracting requirements...');
        toast.info('Extraction started. Small documents: ~1-2 min, Large documents: up to 10 min.');
      } else if (data.requirements) {
        // Synchronous response (backward compatibility)
        setLoading(false);
        setPollingStatus(null);
        
        if (data.requirements.length > 0) {
          setExtractedReqs(data.requirements);
          toast.success(`Extracted ${data.requirements.length} requirement${data.requirements.length !== 1 ? 's' : ''}`);
        } else {
          toast.warning('No requirements found in the document.');
          setExtractionError({
            code: 'NO_REQUIREMENTS',
            message: 'No requirements could be identified in the document.'
          });
        }
      }
      
    } catch (error) {
      console.error('Extraction error:', error);
      setLoading(false);
      setPollingStatus(null);
      
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.error('Request timed out. Try a smaller document.');
      } else if (error.response?.status === 404) {
        toast.error('Project not found');
      } else {
        toast.error(error.response?.data?.detail || error.response?.data?.message || 'Failed to extract requirements.');
      }
      
      setExtractionError({
        code: 'REQUEST_ERROR',
        message: error.message || 'Network error occurred'
      });
    }
  };

  const [aiAnalysisJob, setAiAnalysisJob] = useState(null);
  const [aiProgress, setAiProgress] = useState(null);

  const handleSaveAll = async () => {
    setLoading(true);
    setAiProgress(null);
    try {
      // Use bulk save endpoint - saves immediately, AI runs in background
      const response = await api.post(`/projects/${projectId}/requirements/bulk`, {
        requirements: extractedReqs
      });
      
      if (response.data.success) {
        const saveTime = response.data.save_time_seconds 
          ? ` (saved in ${response.data.save_time_seconds}s)` 
          : '';
        
        toast.success(`${response.data.saved} requirements saved!${saveTime}`);
        
        // Start polling for AI analysis progress
        if (response.data.job_id) {
          setAiAnalysisJob(response.data.job_id);
          pollAiAnalysisStatus(response.data.job_id);
        }
        
        onSuccess();
      } else if (response.data.error_code === 'LIMIT_REACHED') {
        toast.error(
          <div>
            <p className="font-medium">You have reached your plan limit.</p>
            <p className="text-sm mt-1">{response.data.message}</p>
            <button onClick={() => window.location.href = '/pricing'} className="mt-2 text-sm font-semibold text-blue-600 hover:text-blue-700 underline">
              View Plans & Upgrade →
            </button>
          </div>,
          { duration: 10000 }
        );
      } else {
        toast.error('Failed to save requirements');
      }
    } catch (error) {
      // Fallback to individual saves if bulk endpoint fails
      console.error('Bulk save failed, falling back to individual saves:', error);
      let savedCount = 0;
      let errors = [];
      
      for (const req of extractedReqs) {
        try {
          await api.post(`/projects/${projectId}/requirements`, req);
          savedCount++;
        } catch (err) {
          errors.push(req.req_id || req.title);
        }
      }
      
      if (savedCount === extractedReqs.length) {
        toast.success(`All ${savedCount} requirements saved!`);
        onSuccess();
      } else if (savedCount > 0) {
        toast.success(`Saved ${savedCount} of ${extractedReqs.length} requirements`);
        if (errors.length > 0) {
          toast.warning(`Failed to save: ${errors.join(', ')}`);
        }
        onSuccess();
      } else {
        toast.error('Failed to save requirements');
      }
    } finally {
      setLoading(false);
    }
  };

  const pollAiAnalysisStatus = async (jobId) => {
    let pollCount = 0;
    const maxPolls = 120; // 4 minutes max
    
    const poll = async () => {
      try {
        const response = await api.get(`/ai-analysis-status/${jobId}`);
        const data = response.data;
        
        setAiProgress({
          status: data.status,
          total: data.total_requirements,
          modulesClassified: data.modules_classified,
          qualityScores: data.quality_scores_generated,
          intelligence: data.intelligence_generated,
          completedBatches: data.completed_batches,
          totalBatches: data.total_batches,
          processingTime: data.processing_time
        });
        
        if (data.status === 'completed') {
          toast.success(`AI analysis complete! ${data.updated_count} requirements enriched in ${data.processing_time}s`);
          setAiAnalysisJob(null);
          // Refresh requirements to show updated data
          onSuccess();
          return;
        }
        
        if (pollCount < maxPolls && data.status !== 'failed') {
          pollCount++;
          setTimeout(poll, 2000); // Poll every 2 seconds
        } else if (data.status === 'failed') {
          toast.error('AI analysis failed');
          setAiAnalysisJob(null);
        }
      } catch (error) {
        console.error('AI analysis poll error:', error);
        if (pollCount < 3) {
          pollCount++;
          setTimeout(poll, 3000);
        }
      }
    };
    
    poll();
  };

  const handleReset = () => {
    setExtractedReqs(null);
    setExtractionError(null);
    setFile(null);
    setText('');
    setJobId(null);
    setPollingStatus(null);
    setLoading(false);
  };

  return (
    <>
      <DialogHeader><DialogTitle>Upload & Extract Requirements</DialogTitle></DialogHeader>
      <div className="space-y-4">
        {!extractedReqs ? (
          <>
            <div>
              <Label htmlFor="file">Upload Document (PDF/DOCX)</Label>
              <Input 
                id="file" 
                type="file" 
                accept=".pdf,.docx" 
                onChange={(e) => {
                  setFile(e.target.files[0]);
                  setExtractionError(null);
                }} 
                className="mt-1.5" 
                data-testid="upload-file-input"
                disabled={loading}
              />
              <p className="text-xs text-muted-foreground mt-1">Max recommended: 5 pages or 15,000 characters</p>
            </div>
            <div className="text-center text-sm text-muted-foreground">OR</div>
            <div>
              <Label htmlFor="text">Paste Requirements Text</Label>
              <Textarea 
                id="text" 
                value={text} 
                onChange={(e) => {
                  setText(e.target.value);
                  setExtractionError(null);
                }} 
                placeholder="Paste your requirements here..." 
                rows={6} 
                data-testid="paste-text-input"
                disabled={loading}
              />
              {text && (
                <p className="text-xs text-muted-foreground mt-1">
                  {text.length.toLocaleString()} characters
                  {text.length > 15000 && <span className="text-amber-500 ml-1">(exceeds recommended limit)</span>}
                </p>
              )}
            </div>
            
            {/* Show extraction error if any */}
            {extractionError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-700">{extractionError.message}</p>
                {extractionError.code === 'TEXT_TOO_LONG' && (
                  <p className="text-xs text-red-600 mt-1">Tip: Split your document into smaller sections and extract each separately.</p>
                )}
              </div>
            )}
            
            {/* Show polling status */}
            {loading && pollingStatus && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center gap-3">
                  <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-blue-700">{pollingStatus}</p>
                    <p className="text-xs text-blue-600">AI is analyzing your document.</p>
                  </div>
                </div>
                {extractionProgress && extractionProgress.progress > 0 && (
                  <div className="mt-3">
                    <div className="w-full bg-blue-100 rounded-full h-2">
                      <div className="bg-blue-600 h-2 rounded-full transition-all duration-500" style={{ width: `${Math.min(extractionProgress.progress, 100)}%` }} data-testid="extraction-progress-bar" />
                    </div>
                    <p className="text-xs text-blue-500 mt-1 text-right">{extractionProgress.progress}%</p>
                  </div>
                )}
              </div>
            )}
            
            <div className="flex gap-2">
              {loading && (
                <Button variant="outline" onClick={handleReset} className="flex-1">
                  Cancel
                </Button>
              )}
              <Button 
                onClick={handleExtract} 
                disabled={loading || (!file && !text)} 
                className={`gap-2 ${loading ? 'flex-1' : 'w-full'}`}
                data-testid="extract-requirements-button"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                {loading ? 'Extracting...' : 'Extract with AI'}
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="max-h-96 overflow-y-auto space-y-3">
              <p className="text-sm text-muted-foreground">{extractedReqs.length} requirements extracted. Review and save:</p>
              {extractedReqs.map((req, idx) => (
                <Card key={idx} className="p-4">
                  <div className="text-sm font-mono text-azure mb-1">{req.req_id}</div>
                  <div className="font-medium text-sm mb-1">{req.title}</div>
                  <div className="text-xs text-muted-foreground">{req.module}</div>
                </Card>
              ))}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleReset} disabled={loading} className="flex-1">
                Back
              </Button>
              <Button onClick={handleSaveAll} disabled={loading} className="flex-1" data-testid="save-all-requirements-button">
                {loading ? 'Saving...' : 'Save All Requirements'}
              </Button>
            </div>
            
            {/* AI Analysis Progress */}
            {aiAnalysisJob && aiProgress && (
              <div className="mt-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                  <span className="text-sm font-medium text-blue-700">
                    AI Analysis in Progress
                    {aiProgress.status === 'completed' ? ' - Complete!' : '...'}
                  </span>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Requirements extracted:</span>
                    <span className="font-medium text-slate-800">{aiProgress.total}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Modules classified:</span>
                    <span className="font-medium text-emerald-600">{aiProgress.modulesClassified}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Quality scores generated:</span>
                    <span className="font-medium text-amber-600">{aiProgress.qualityScores}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Intelligence generated:</span>
                    <span className="font-medium text-indigo-600">{aiProgress.intelligence}</span>
                  </div>
                  {aiProgress.totalBatches > 0 && (
                    <div className="mt-2 pt-2 border-t border-blue-200">
                      <div className="flex justify-between text-xs text-slate-500">
                        <span>Batch progress:</span>
                        <span>{aiProgress.completedBatches} / {aiProgress.totalBatches}</span>
                      </div>
                      <div className="mt-1 h-2 bg-blue-100 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-blue-500 transition-all duration-300"
                          style={{ width: `${(aiProgress.completedBatches / aiProgress.totalBatches) * 100}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

export const RequirementsTab = ({ projectId, project, requirements, onUpdate, usage }) => {
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [requirementToDelete, setRequirementToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [scenarios, setScenarios] = useState({});
  const [scenarioLoading, setScenarioLoading] = useState(false);
  const [scenarioProgress, setScenarioProgress] = useState(null);
  const scenarioPollRef = useRef(null);

  // Fetch scenarios on mount and when requirements change
  useEffect(() => {
    fetchScenarios();
    return () => { if (scenarioPollRef.current) clearInterval(scenarioPollRef.current); };
  }, [projectId, requirements.length]);

  const fetchScenarios = async () => {
    try {
      const res = await api.get(`/projects/${projectId}/scenarios`);
      const grouped = {};
      (res.data || []).forEach(s => {
        if (!grouped[s.requirement_id]) grouped[s.requirement_id] = [];
        grouped[s.requirement_id].push(s);
      });
      setScenarios(grouped);
      return Object.keys(grouped).length;
    } catch { return 0; }
  };

  const handleGenerateScenarios = async () => {
    // Prevent duplicate triggers
    if (scenarioLoading) return;

    setScenarioProgress(null);
    try {
      const res = await api.post(`/projects/${projectId}/generate-scenarios`);
      const jobId = res.data.job_id;

      // If all requirements already covered — no job needed
      if (!jobId || res.data.already_covered) {
        toast.info(res.data.message || 'All requirements already have scenarios. Nothing to regenerate.');
        return;
      }

      // Only start loading state AFTER we confirm a job was created
      setScenarioLoading(true);
      const completedRef = { done: false };
      const startTime = Date.now();
      const TIMEOUT_MS = 2 * 60 * 1000; // 2 minutes safety timeout

      toast.success(`Generating scenarios for ${res.data.uncovered_requirements || res.data.total_requirements} requirements...`);

      scenarioPollRef.current = setInterval(async () => {
        if (completedRef.done) return;

        // Safety timeout: auto-stop after 2 minutes with no completion
        if (Date.now() - startTime > TIMEOUT_MS) {
          completedRef.done = true;
          clearInterval(scenarioPollRef.current);
          scenarioPollRef.current = null;
          setScenarioLoading(false);
          setScenarioProgress(null);
          toast.error('Scenario regeneration failed. Please retry.');
          return;
        }

        try {
          const status = await api.get(`/scenario-status/${jobId}`);
          const data = status.data;
          setScenarioProgress(data);
          if (data.status === 'completed') {
            if (completedRef.done) return;
            completedRef.done = true;
            clearInterval(scenarioPollRef.current);
            scenarioPollRef.current = null;

            const count = await fetchScenarios();
            if (count === 0 && data.scenarios_generated > 0) {
              await new Promise(r => setTimeout(r, 1000));
              await fetchScenarios();
            }

            setScenarioLoading(false);
            setScenarioProgress(null);
            toast.success(`Generated ${data.scenarios_generated} scenarios`);
            onUpdate();
          } else if (data.status === 'failed') {
            if (completedRef.done) return;
            completedRef.done = true;
            clearInterval(scenarioPollRef.current);
            scenarioPollRef.current = null;
            setScenarioLoading(false);
            setScenarioProgress(null);
            toast.error(data.error || 'Scenario generation failed');
          }
        } catch { /* ignore poll errors */ }
      }, 3000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start scenario generation');
      setScenarioLoading(false);
    }
  };

  const handleDeleteClick = (req) => {
    setRequirementToDelete(req);
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (!requirementToDelete) return;
    
    setDeleting(true);
    try {
      const response = await api.delete(`/projects/${projectId}/requirements/${requirementToDelete.id}`);
      toast.success(`Requirement ${requirementToDelete.req_id} deleted successfully`);
      if (response.data.deleted_testcases_count > 0) {
        toast.info(`${response.data.deleted_testcases_count} associated test case(s) also deleted`);
      }
      setShowDeleteDialog(false);
      setRequirementToDelete(null);
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete requirement');
    } finally {
      setDeleting(false);
    }
  };

  // Derive coverage state from scenarios + requirements
  const coveredReqIds = new Set(Object.keys(scenarios));
  const uncoveredReqs = requirements.filter(r => !coveredReqIds.has(r.req_id));
  const hasScenarios = coveredReqIds.size > 0;
  const allCovered = hasScenarios && uncoveredReqs.length === 0;
  const atLimit = usage?.scenarios?.used >= usage?.scenarios?.limit;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-heading font-semibold text-blueprint">Requirements</h2>
        <div className="flex gap-2">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button 
                    variant="outline" 
                    className="gap-2" 
                    onClick={handleGenerateScenarios} 
                    disabled={scenarioLoading || requirements.length === 0 || allCovered || atLimit}
                    data-testid="generate-scenarios-button"
                  >
                    {scenarioLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
                    {scenarioLoading ? 'Generating...' : hasScenarios ? 'Generate Missing Scenarios' : 'Generate Scenarios'}
                  </Button>
                </span>
              </TooltipTrigger>
              {allCovered && !scenarioLoading && (
                <TooltipContent data-testid="scenarios-covered-tooltip">
                  <p>All requirements already have scenarios</p>
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
          <Button variant="outline" className="gap-2" onClick={() => setShowUploadDialog(true)} data-testid="upload-requirements-button">
            <Upload className="h-4 w-4" />Upload Document
          </Button>
          <Button className="gap-2 bg-blueprint hover:bg-blueprint/90" onClick={() => setShowAddDialog(true)} data-testid="add-requirement-button">
            <Plus className="h-4 w-4" />Add Requirement
          </Button>
        </div>
      </div>

      {/* Scenario generation progress */}
      {scenarioProgress && scenarioLoading && (
        <div className="mb-4 p-4 bg-gradient-to-r from-violet-50 to-purple-50 border border-violet-200 rounded-lg" data-testid="scenario-progress">
          <div className="flex items-center gap-3">
            <Loader2 className="h-5 w-5 animate-spin text-violet-600 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-violet-700">Generating Scenarios...</p>
              <div className="mt-1.5 h-1.5 bg-violet-100 rounded-full overflow-hidden">
                <div className="h-full bg-violet-500 transition-all duration-300" 
                  style={{ width: `${((scenarioProgress.processed || 0) / Math.max(1, scenarioProgress.total_requirements)) * 100}%` }} />
              </div>
            </div>
            <span className="text-xs font-mono text-violet-600">
              {Math.round(((scenarioProgress.processed || 0) / Math.max(1, scenarioProgress.total_requirements)) * 100)}%
            </span>
          </div>
        </div>
      )}

      {requirements.length === 0 ? (
        <Card className="p-12 text-center border-dashed" data-testid="requirements-empty-state">
          <FileText className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
          <h3 className="text-xl font-heading font-semibold mb-2 text-blueprint">No requirements yet</h3>
          <p className="text-muted-foreground mb-6">Add requirements manually or upload a document to extract them</p>
        </Card>
      ) : (
        <div className="space-y-4">
          {requirements.map((req) => (
            <Card key={req.id} className="p-6 hover:border-azure/50 transition-all" data-testid={`requirement-card-${req.req_id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <span className="px-2 py-1 bg-azure/10 text-azure rounded font-mono text-sm font-medium">{req.req_id}</span>
                  <span className="px-2 py-1 bg-muted rounded text-sm font-medium">{req.module}</span>
                  {/* Quality Score Badge */}
                  {req.quality_score !== null && req.quality_score !== undefined && (
                    <span 
                      className={`px-2 py-1 rounded text-sm font-medium ${
                        req.quality_score >= 80 
                          ? 'bg-emerald-100 text-emerald-700' 
                          : req.quality_score >= 60 
                            ? 'bg-amber-100 text-amber-700' 
                            : 'bg-red-100 text-red-700'
                      }`}
                      title={req.quality_feedback ? `Strengths: ${req.quality_feedback.strengths?.join(', ') || 'N/A'}` : ''}
                      data-testid={`quality-score-${req.req_id}`}
                    >
                      Quality: {req.quality_score}%
                    </span>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDeleteClick(req)}
                  className="text-destructive hover:text-destructive hover:bg-destructive/10"
                  data-testid={`delete-requirement-${req.req_id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              <h3 className="text-lg font-heading font-semibold text-blueprint mb-2">{req.title}</h3>
              <p className="text-sm text-muted-foreground mb-3">{req.description}</p>
              
              {/* Quality Feedback */}
              {req.quality_feedback && (req.quality_feedback.strengths?.length > 0 || req.quality_feedback.improvements?.length > 0) && (
                <div className="mb-3 p-3 bg-slate-50 rounded-lg text-sm">
                  {req.quality_feedback.strengths?.length > 0 && (
                    <div className="mb-2">
                      <span className="text-emerald-600 font-medium">Strengths: </span>
                      <span className="text-slate-600">{req.quality_feedback.strengths.join(', ')}</span>
                    </div>
                  )}
                  {req.quality_feedback.improvements?.length > 0 && (
                    <div>
                      <span className="text-amber-600 font-medium">Improvements: </span>
                      <span className="text-slate-600">{req.quality_feedback.improvements.join(', ')}</span>
                    </div>
                  )}
                </div>
              )}
              
              <div className="mt-3 pt-3 border-t border-border">
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Acceptance Criteria</p>
                {Array.isArray(req.acceptance_criteria) ? (
                  <ul className="list-disc list-inside space-y-1">
                    {req.acceptance_criteria.map((criteria, idx) => (
                      <li key={idx} className="text-sm">{criteria}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm">{req.acceptance_criteria}</p>
                )}
              </div>

              {/* Scenarios */}
              {scenarios[req.req_id] && scenarios[req.req_id].length > 0 && (
                <div className="mt-3 pt-3 border-t border-border" data-testid={`scenarios-${req.req_id}`}>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">
                    Test Scenarios ({scenarios[req.req_id].length})
                  </p>
                  <div className="space-y-1.5">
                    {scenarios[req.req_id].map((s) => (
                      <div key={s.id} className="flex items-center gap-2 text-sm" data-testid={`scenario-${s.scenario_id}`}>
                        <ScenarioTypeBadge type={s.type} />
                        <span className="text-slate-700">{s.title}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {showAddDialog && (
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent>
            <AddRequirementDialog projectId={projectId} project={project} onSuccess={() => { setShowAddDialog(false); onUpdate(); }} />
          </DialogContent>
        </Dialog>
      )}

      {showUploadDialog && (
        <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
          <DialogContent>
            <UploadRequirementDialog projectId={projectId} project={project} onSuccess={() => { setShowUploadDialog(false); onUpdate(); }} usage={usage} />
          </DialogContent>
        </Dialog>
      )}

      {showDeleteDialog && requirementToDelete && (
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Requirement</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete requirement <strong>{requirementToDelete.req_id}</strong>: "{requirementToDelete.title}"?
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <p className="text-sm text-muted-foreground">
                This action cannot be undone. This will permanently delete the requirement and all associated test cases.
              </p>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowDeleteDialog(false)}
                disabled={deleting}
                data-testid="cancel-delete-button"
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeleteConfirm}
                disabled={deleting}
                data-testid="confirm-delete-button"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};